import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Button, 
  Dialog, DialogPanel, DialogTitle, Transition, TransitionChild,
  Field, Label, Input, Textarea, Listbox, ListboxButton, ListboxOptions, ListboxOption
} from '@headlessui/react';
import { Fragment } from 'react';
import { 
  FaArrowLeft, FaEdit, FaTrash, FaPlus, FaMobile, FaGlobe, FaDesktop, 
  FaFolder, FaDownload, FaExternalLinkAlt, FaCog, FaBars, FaTimes,
  FaChevronDown, FaCheck, FaUpload, FaImage, FaAndroid, FaApple, FaInfoCircle,
  FaHeadset, FaLink, FaFileAlt, FaAd, FaGlobeAmericas
} from 'react-icons/fa';
import { authenticatedFetch, isTokenValid, redirectToLogin, getBaseUrl, getUser } from '../utils/tokenUtils';
import ProductFormModal from '../components/ProductFormModal';
import ProjectForm from '../components/ProjectForm';
import DashboardLayout from '../components/DashboardLayout';
import StatsCard from '../components/StatsCard';
import FeedbackSupportModal from '../components/FeedbackSupportModal';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [project, setProject] = useState(null);
  const [products, setProducts] = useState([]);
  const [modules, setModules] = useState([]);
  const [supportModules, setSupportModules] = useState([]);
  const [shortLinks, setShortLinks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [internalAds, setInternalAds] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  
  // Toast notification states
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Toast notification functions
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      
      // Ana proje verisini al
      const projectRes = await authenticatedFetch(`/api/dashboard/user/projects/${projectId}`);
      const projectData = await projectRes.json();
      
      if (!projectData.project) {
        throw new Error('Project not found');
      }
      
      setProject(projectData.project);
      
      // Diğer verileri paralel olarak al (hata olursa boş array kullan)
      try {
        const [productsRes, modulesRes, supportModulesRes, shortLinksRes, documentsRes, internalAdsRes, landingPagesRes] = await Promise.allSettled([
          authenticatedFetch(`/api/dashboard/user/projects/${projectId}/products`),
          authenticatedFetch(`/api/dashboard/user/projects/${projectId}/modules`),
          authenticatedFetch(`/api/support/user/support-modules?projectId=${projectId}`),
          authenticatedFetch(`/api/shortlink?projectId=${projectId}`),
          authenticatedFetch(`/api/dashboard/user/projects/${projectId}/documents`),
          authenticatedFetch(`/api/internal-ads/placements?projectId=${projectId}`),
          authenticatedFetch(`/api/landing-pages?projectId=${projectId}`)
        ]);

        // Products
        if (productsRes.status === 'fulfilled') {
          const productsData = await productsRes.value.json();
          setProducts(productsData.products || []);
        } else {
          setProducts([]);
        }

        // Modules
        if (modulesRes.status === 'fulfilled') {
          const modulesData = await modulesRes.value.json();
          setModules(modulesData.modules || []);
        } else {
          setModules([]);
        }

        // Support Modules
        if (supportModulesRes.status === 'fulfilled') {
          const supportModulesData = await supportModulesRes.value.json();
          setSupportModules(supportModulesData.supportModules || []);
        } else {
          setSupportModules([]);
        }

        // Short Links
        if (shortLinksRes.status === 'fulfilled') {
          const shortLinksData = await shortLinksRes.value.json();
          setShortLinks(shortLinksData.shortLinks || []);
        } else {
          setShortLinks([]);
        }

        // Documents
        if (documentsRes.status === 'fulfilled') {
          const documentsData = await documentsRes.value.json();
          setDocuments(documentsData.documents || []);
        } else {
          setDocuments([]);
        }

        // Internal Ads
        if (internalAdsRes.status === 'fulfilled') {
          const internalAdsData = await internalAdsRes.value.json();
          setInternalAds(internalAdsData.placements || []);
        } else {
          setInternalAds([]);
        }

        // Landing Pages
        if (landingPagesRes.status === 'fulfilled') {
          const landingPagesData = await landingPagesRes.value.json();
          setLandingPages(landingPagesData.landingPages || []);
        } else {
          setLandingPages([]);
        }

      } catch (secondaryError) {
        console.warn('Some secondary data failed to load:', secondaryError);
        // Ana proje verisi yüklendi, sadece ikincil veriler hata verdi
        setProducts([]);
        setModules([]);
        setSupportModules([]);
        setShortLinks([]);
        setDocuments([]);
        setInternalAds([]);
        setLandingPages([]);
      }

    } catch (error) {
      console.error('Error fetching project data:', error);
      if (error.message === 'Authentication failed') {
        navigate('/login');
      } else if (error.message === 'Project not found') {
        // Project not found - bu durumda error page gösterilecek
        return;
      } else {
        showToast('Error loading project details.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load user data
    const userData = getUser();
    if (userData) {
      setUser(userData);
      fetchProjectData();
    } else {
      navigate('/login');
    }
  }, [projectId]);

  const handleCreateProduct = async (productData) => {
    setIsSubmitting(true);
    try {
      const response = await authenticatedFetch(`/api/dashboard/user/projects/${projectId}/products`, {
        method: 'POST',
        body: productData
      });

      const result = await response.json();
      setProducts(prev => [...prev, result.product]);
      setShowProductForm(false);
      setEditingProduct(null);
      fetchProjectData(); // Refresh project data for updated counts
    } catch (error) {
      console.error('Create product error:', error);
      if (error.message === 'Authentication failed') {
        navigate('/login');
      } else {
        showToast('Error occurred while creating product.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProduct = async (productData) => {
    setIsSubmitting(true);
    try {
      const response = await authenticatedFetch(`/api/dashboard/user/products/${editingProduct._id}`, {
        method: 'PUT',
        body: productData
      });

      const result = await response.json();
      setProducts(prev => prev.map(p => p._id === editingProduct._id ? result.product : p));
      setShowProductForm(false);
      setEditingProduct(null);
    } catch (error) {
      console.error('Update product error:', error);
      if (error.message === 'Authentication failed') {
        navigate('/login');
      } else {
        showToast('Error occurred while updating product.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProject = async (projectData) => {
    setIsSubmitting(true);
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }
      
      console.log('ProjectDetail - Updating project:', projectId);
      console.log('ProjectDetail - Project data:', projectData);
      console.log('ProjectDetail - Project data logo:', projectData.logo);
      
      const response = await authenticatedFetch(`/api/dashboard/user/projects/${projectId}`, {
        method: 'PUT',
        body: projectData
      });

      console.log('ProjectDetail - Update response status:', response.status);
      const result = await response.json();
      console.log('ProjectDetail - Update result:', result);
      console.log('ProjectDetail - Updated project logo:', result.project?.logo);
      
      setProject(result.project);
      setShowProjectForm(false);
    } catch (error) {
      console.error('ProjectDetail - Update project error:', error);
      if (error.message === 'Authentication failed') {
        navigate('/login');
      } else {
        showToast('Error occurred while updating project.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProject = async () => {
    if (deleteConfirmText !== project.name) {
      showToast('Project name does not match. Please type the exact project name to confirm deletion.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await authenticatedFetch(`/api/dashboard/user/projects/${projectId}`, {
        method: 'DELETE'
      });

      // Navigate back to dashboard after successful deletion
      navigate('/dashboard');
    } catch (error) {
      console.error('Delete project error:', error);
      if (error.message === 'Authentication failed') {
        navigate('/login');
      } else {
        showToast('Error occurred while deleting project.', 'error');
      }
    } finally {
      setIsSubmitting(false);
      setShowDeleteDialog(false);
      setDeleteConfirmText('');
    }
  };

  const handleDeleteProduct = async (product) => {
    try {
      await authenticatedFetch(`/api/dashboard/user/products/${product._id}`, {
        method: 'DELETE'
      });

      setProducts(prev => prev.filter(p => p._id !== product._id));
      fetchProjectData(); // Refresh project data for updated counts
    } catch (error) {
      console.error('Delete product error:', error);
      if (error.message === 'Authentication failed') {
        navigate('/login');
      } else {
        showToast('Error occurred while deleting product.', 'error');
      }
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'android':
        return <FaAndroid className="w-4 h-4 text-green-400" />;
      case 'ios':
        return <FaApple className="w-4 h-4 text-gray-300" />;
      case 'web':
        return <FaGlobe className="w-4 h-4 text-blue-400" />;
      default:
        return <FaDesktop className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'development':
        return 'bg-blue-900 text-blue-300 border-blue-700';
      case 'testing':
        return 'bg-yellow-900 text-yellow-300 border-yellow-700';
      case 'published':
        return 'bg-green-900 text-green-300 border-green-700';
      case 'suspended':
        return 'bg-red-900 text-red-300 border-red-700';
      case 'archived':
        return 'bg-gray-700 text-gray-300 border-gray-600';
      default:
        return 'bg-gray-700 text-gray-300 border-gray-600';
    }
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    try {
      const response = await authenticatedFetch('/api/contact', {
        method: 'POST',
        body: feedbackData
      });

      if (response.ok) {
        showToast('Feedback submitted successfully!', 'success');
      } else {
        showToast('Failed to submit feedback. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showToast('Failed to submit feedback. Please try again.', 'error');
    }
  };

  if (loading) {
    return (
      <DashboardLayout
        user={user}
        onLogout={handleLogout}
        onShowLogoutDialog={() => setShowLogoutDialog(true)}
        onShowFeedbackModal={() => setShowFeedbackModal(true)}
        onUpgradeClick={() => {}}
        headerProps={{
          customTitle: 'Project Details',
          showBackButton: true,
          onBackClick: () => navigate('/dashboard/projects')
        }}
      >
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading project details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout
        user={user}
        onLogout={handleLogout}
        onShowLogoutDialog={() => setShowLogoutDialog(true)}
        onShowFeedbackModal={() => setShowFeedbackModal(true)}
        onUpgradeClick={() => {}}
        headerProps={{
          customTitle: 'Project Details',
          showBackButton: true,
          onBackClick: () => navigate('/dashboard/projects')
        }}
      >
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaFolder className="w-8 h-8 text-gray-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Project Not Found</h1>
            <p className="text-gray-400 mb-6">The project you're looking for doesn't exist or you don't have access to it.</p>
            <Button
              onClick={() => navigate('/dashboard/projects')}
              className="inline-flex items-center px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors duration-200 data-hover:bg-gray-100 data-focus:ring-white"
            >
              <FaArrowLeft className="w-4 h-4 mr-2" />
              Back to Projects
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      user={user}
      onLogout={handleLogout}
      onShowLogoutDialog={() => setShowLogoutDialog(true)}
      onShowFeedbackModal={() => setShowFeedbackModal(true)}
      onUpgradeClick={() => {}}
      headerProps={{
        customTitle: project?.name || 'Project Details',
        showBackButton: true,
        onBackClick: () => navigate('/dashboard/projects'),
        rightActions: (
          <div className="flex items-center space-x-2">
            {/* Products Info */}
            <div className="relative group">
              <div className="flex items-center px-3 py-1 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer">
                <FaMobile className="w-3 h-3 text-gray-400 mr-2" />
                <span className="text-xs text-gray-300">
                  {products.length} Product{products.length !== 1 ? 's' : ''}
                </span>
              </div>
              
              {/* Hover Tooltip */}
              <div className="absolute right-0 top-full mt-2 w-48 bg-black border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="p-2">
                  {products.length === 0 ? (
                    <p className="text-xs text-gray-400">No products yet</p>
                  ) : (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {products.map((product) => (
                        <div 
                          key={product._id} 
                          className="flex items-center cursor-pointer hover:bg-gray-800 rounded-lg p-1 transition-colors duration-200"
                          onClick={() => {
                            setEditingProduct(product);
                            setShowProductForm(true);
                          }}
                        >
                          <div className="w-6 h-6 bg-gray-700 rounded flex items-center justify-center mr-2">
                            {getPlatformIcon(product.platform)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-white truncate font-medium">{product.name || 'Unnamed Product'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(project.status)}`}>
              {project.status}
            </span>
            <Button
              onClick={() => setShowProjectForm(true)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200 data-hover:bg-gray-800 data-focus:ring-white"
            >
              <FaEdit className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => setShowDeleteDialog(true)}
              className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-900/50 rounded-lg transition-colors duration-200 data-hover:bg-red-900/50 data-focus:ring-white"
            >
              <FaTrash className="w-4 h-4" />
            </Button>
          </div>
        )
      }}
    >

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <StatsCard
            title="Products"
            value={products.length}
            icon={FaMobile}
          />
          
          <StatsCard
            title="Support Modules"
            value={supportModules.length}
            icon={FaHeadset}
          />
          
          <StatsCard
            title="Short Links"
            value={shortLinks.length}
            icon={FaLink}
          />
          
          <StatsCard
            title="Documents"
            value={documents.length}
            icon={FaFileAlt}
          />
          
          <StatsCard
            title="Internal Ads"
            value={internalAds.length}
            icon={FaAd}
          />
          
          <StatsCard
            title="Landing Pages"
            value={landingPages.length}
            icon={FaGlobeAmericas}
          />
        </div>

        {/* Products Section */}
        <div className="bg-black rounded-2xl border border-gray-800 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-white">Products</h3>
                  <Button
                    onClick={() => setShowProductForm(true)}
                    className="group flex items-center px-6 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-700 transition-all duration-200 data-hover:bg-gray-700 data-focus:ring-white border border-gray-700"
                  >
                    <FaPlus className="mr-2 group-hover:rotate-90 transition-transform duration-200" />
                    New Product
                  </Button>
                </div>

                {products.length === 0 ? (
                  <div className="text-center py-12">
                    <FaMobile className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">No products yet</h3>
                    <p className="text-gray-400 mb-4">Get started by adding your first product.</p>
                    <Button
                      onClick={() => setShowProductForm(true)}
                      className="group flex items-center mx-auto px-6 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-700 transition-all duration-200 data-hover:bg-gray-700 data-focus:ring-white border border-gray-700"
                    >
                      <FaPlus className="mr-2 group-hover:rotate-90 transition-transform duration-200" />
                      New Product
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {products.map((product) => (
                      <div 
                        key={product._id} 
                        className="bg-black rounded-lg border border-gray-700 p-4 cursor-pointer hover:border-gray-600 hover:bg-gray-900/50 transition-all duration-200"
                        onClick={() => {
                          setEditingProduct(product);
                          setShowProductForm(true);
                        }}
                        title="Click to view/edit product details"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center mr-3">
                              {getPlatformIcon(product.platform)}
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-white">{product.name || 'Unnamed Product'}</h4>
                            </div>
                          </div>
                          
                          {/* Product ID - Sağda en üstte, icon ile aynı hizada */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent card click
                              navigator.clipboard.writeText(product._id);
                              showToast('Product ID copied!', 'success');
                            }}
                            className="text-xs bg-gray-800 text-gray-300 px-2 py-1 rounded hover:bg-gray-700 transition-colors cursor-pointer font-mono"
                            title="Click to copy Product ID"
                          >
                            {product._id}
                          </button>
                        </div>
                        
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>v{product.version}</span>
                          <span className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(product.status)}`}>
                            {product.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
        </div>

      {/* Product Form Modal */}
      <ProductFormModal
        isOpen={showProductForm}
        onClose={() => {
          setShowProductForm(false);
        }}
        onSubmit={editingProduct ? handleUpdateProduct : handleCreateProduct}
        onDelete={handleDeleteProduct}
        project={project}
        product={editingProduct}
        isLoading={isSubmitting}
      />

      {/* Project Form Modal */}
      <ProjectForm
        isOpen={showProjectForm}
        onClose={() => setShowProjectForm(false)}
        onSubmit={(data) => handleUpdateProject(data)}
        project={project}
        isLoading={isSubmitting}
      />

      {/* Delete Confirmation Dialog */}
      <Transition appear show={showDeleteDialog} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowDeleteDialog(false)}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-75" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-gray-900 border border-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                  <DialogTitle as="h3" className="text-lg font-medium leading-6 text-white mb-4">
                    Delete Project
                  </DialogTitle>
                  
                  <div className="mb-6">
                    <p className="text-sm text-gray-300 mb-4">
                      This action cannot be undone. This will permanently delete the project and all associated products and modules.
                    </p>
                    <p className="text-sm text-gray-300 mb-2">
                      Please type <span className="font-semibold text-white">{project?.name}</span> to confirm:
                    </p>
                    <Input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                      placeholder="Project name"
                    />
                  </div>

                  <div className="flex justify-end space-x-3">
                    <Button
                      type="button"
                      onClick={() => {
                        setShowDeleteDialog(false);
                        setDeleteConfirmText('');
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors duration-200"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleDeleteProject}
                      disabled={deleteConfirmText !== project?.name || isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {isSubmitting ? 'Deleting...' : 'Delete Project'}
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

        {/* Toast Notification */}
        {toast.show && (
          <div className="fixed top-4 right-4 z-[60]">
            <div className={`px-4 py-3 rounded-lg shadow-lg border transition-all duration-300 ${
              toast.type === 'success' 
                ? 'bg-green-900 border-green-700 text-green-300' 
                : 'bg-red-900 border-red-700 text-red-300'
            }`}>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  toast.type === 'success' ? 'bg-green-400' : 'bg-red-400'
                }`}></div>
                <span className="text-sm font-medium">{toast.message}</span>
              </div>
            </div>
          </div>
        )}

      {/* Logout Dialog */}
      {showLogoutDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">Logout</h3>
            <p className="text-gray-300 mb-6">Are you sure you want to logout?</p>
            <div className="flex space-x-4">
              <button
                onClick={() => setShowLogoutDialog(false)}
                className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Support Modal */}
      <FeedbackSupportModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
        user={user}
      />
    </DashboardLayout>
  );
};

export default ProjectDetail;
